import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Copy, ExternalLink, Palette, Type, Image as ImageIcon, Link as LinkIcon, FileText, Library } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { readFileAsDataURL } from "@/lib/format";

const KIND_META = {
  logo:  { label: "Logo",   icon: ImageIcon },
  color: { label: "Color",  icon: Palette },
  font:  { label: "Font",   icon: Type },
  link:  { label: "Link",   icon: LinkIcon },
  file:  { label: "File",   icon: FileText },
};

export default function BrandLibrary({ embedded = false }) {
  const { activeWorkspaceId } = useAuth();
  const [assets, setAssets] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterClient, setFilterClient] = useState("");
  const [open, setOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = async () => {
    if (!activeWorkspaceId) return;
    const q = filterClient ? `?client_id=${filterClient}` : "";
    const { data } = await api.get(`/workspaces/${activeWorkspaceId}/brand-assets${q}`);
    setAssets(data);
  };

  useEffect(() => { load(); }, [activeWorkspaceId, filterClient]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/clients`).then((r) => setClients(r.data));
  }, [activeWorkspaceId]);

  const onDelete = async () => {
    const aid = confirmId;
    if (!aid) return;
    try {
      await api.delete(`/brand-assets/${aid}`);
      toast.success("Removed");
      setConfirmId(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not delete"));
    }
  };

  const toolbar = (
    <div className="flex gap-2 flex-wrap">
      <Select value={filterClient || "all"} onValueChange={(v) => setFilterClient(v === "all" ? "" : v)}>
        <SelectTrigger className="h-10 w-44" data-testid="brand-filter-client"><SelectValue placeholder="All clients" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All clients</SelectItem>
          {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <AddDialog open={open} setOpen={setOpen} clients={clients} onCreated={load} />
    </div>
  );

  const grid = (
    <>
      {assets.length === 0 ? (
        <EmptyState
          title="Your brand library is empty"
          description="Add logos, colors, fonts, and links so designers and reviewers have one place to check."
          icon={Library}
          testid="brand-empty"
          action={<Button onClick={() => setOpen(true)} className="rounded-none" data-testid="brand-empty-add-btn">Add your first asset</Button>}
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="brand-asset-grid">
          {assets.map((a) => <AssetCard key={a.id} asset={a} clients={clients} onDelete={() => setConfirmId(a.id)} />)}
        </div>
      )}
      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent data-testid="brand-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this brand asset?</AlertDialogTitle>
            <AlertDialogDescription>
              The asset will be removed from this workspace. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="brand-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="brand-delete-confirm-btn" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  if (embedded) {
    return (
      <div className="px-4 sm:px-6 md:px-10 pt-6 pb-10 space-y-4" data-testid="brand-library-embedded">
        <div className="flex justify-end">{toolbar}</div>
        {grid}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Brand library"
        description="Logos, colors, fonts, and references for every client. One source of truth."
        testid="brand-library-header"
        actions={toolbar}
      />
      <div className="px-4 sm:px-6 md:px-10 py-8">
        {grid}
      </div>
    </>
  );
}

function AssetCard({ asset, clients, onDelete }) {
  const Icon = KIND_META[asset.kind]?.icon || FileText;
  const client = clients.find((c) => c.id === asset.client_id);
  const copy = () => { navigator.clipboard.writeText(asset.value); toast.success("Copied"); };

  return (
    <div className="border border-border bg-card flex flex-col group" data-testid={`brand-asset-${asset.id}`}>
      <div className="aspect-square bg-muted relative flex items-center justify-center overflow-hidden">
        {asset.kind === "color" ? (
          <div className="w-full h-full" style={{ background: asset.value }} />
        ) : asset.kind === "logo" ? (
          <img src={asset.value} alt={asset.label} className="max-w-[80%] max-h-[80%] object-contain" />
        ) : asset.kind === "font" ? (
          <div className="text-3xl px-3 text-center" style={{ fontFamily: `'${asset.value}', sans-serif` }}>Aa</div>
        ) : asset.kind === "link" ? (
          <LinkIcon className="w-10 h-10 text-muted-foreground" />
        ) : (
          <Icon className="w-10 h-10 text-muted-foreground" />
        )}
        <button
          onClick={onDelete}
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition bg-black/60 text-white p-1 rounded-sm"
          data-testid={`brand-delete-${asset.id}`}
          title="Remove"
        ><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      <div className="px-3 py-2 border-t border-border">
        <div className="flex items-center gap-1.5 text-xs">
          <Icon className="w-3 h-3 text-muted-foreground shrink-0" />
          <span className="font-semibold truncate flex-1">{asset.label}</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] text-muted-foreground truncate">{client?.name || "Workspace"}</span>
          <div className="flex gap-1">
            <button onClick={copy} className="text-[10px] text-muted-foreground hover:text-primary" data-testid={`brand-copy-${asset.id}`} title="Copy value">
              <Copy className="w-3 h-3" />
            </button>
            {asset.kind === "link" && (
              <a href={asset.value} target="_blank" rel="noopener noreferrer" className="text-[10px] text-muted-foreground hover:text-primary" title="Open">
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AddDialog({ open, setOpen, clients, onCreated }) {
  const { activeWorkspaceId } = useAuth();
  const [kind, setKind] = useState("color");
  const [label, setLabel] = useState("");
  const [value, setValue] = useState("");
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Max 5 MB per asset");
    const dataUrl = await readFileAsDataURL(f);
    setValue(dataUrl);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!label.trim() || !value.trim()) return toast.error("Label and value are required");
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/brand-assets`, {
        kind,
        label: label.trim(),
        value,
        client_id: clientId || null,
      });
      toast.success("Brand asset added");
      setOpen(false);
      setLabel(""); setValue(""); setClientId(""); setKind("color");
      onCreated && onCreated();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not add asset"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none" data-testid="brand-add-btn"><Plus className="w-4 h-4 mr-2" /> Add asset</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a brand asset</DialogTitle>
          <DialogDescription>Logo, color, font name, link, or reference file.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="label-eyebrow mb-2 inline-block">Type</Label>
            <Select value={kind} onValueChange={(v) => { setKind(v); setValue(""); }}>
              <SelectTrigger className="h-10" data-testid="brand-form-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow mb-2 inline-block">Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Primary brand color" data-testid="brand-form-label" required />
          </div>
          <div>
            <Label className="label-eyebrow mb-2 inline-block">
              {kind === "color" ? "Color (HSL or HEX, e.g. #1a73e8)"
                : kind === "font" ? "Font family name (e.g. Inter, Cabinet Grotesk)"
                : kind === "link" ? "URL (https://...)"
                : "Upload file"}
            </Label>
            {(kind === "logo" || kind === "file") ? (
              <input type="file" onChange={onFileChange} accept={kind === "logo" ? "image/*" : undefined}
                className="block w-full text-sm border border-input file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-foreground file:text-background file:font-bold file:uppercase file:text-xs file:tracking-widest"
                data-testid="brand-form-file" />
            ) : kind === "color" ? (
              <div className="flex gap-2">
                <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="#1a73e8" data-testid="brand-form-value" required />
                <input type="color" value={value.startsWith("#") ? value : "#1a73e8"} onChange={(e) => setValue(e.target.value)} className="h-10 w-12 border border-border" data-testid="brand-form-color-picker" />
              </div>
            ) : (
              <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={kind === "link" ? "https://example.com" : "Inter"} data-testid="brand-form-value" required />
            )}
            {(kind === "logo" || kind === "file") && value && (
              <div className="text-[10px] text-muted-foreground mt-1">{value.slice(0, 50)}…</div>
            )}
          </div>
          <div>
            <Label className="label-eyebrow mb-2 inline-block">Client (optional)</Label>
            <Select value={clientId || "none"} onValueChange={(v) => setClientId(v === "none" ? "" : v)}>
              <SelectTrigger className="h-10" data-testid="brand-form-client"><SelectValue placeholder="Workspace-wide" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Workspace-wide</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-none" data-testid="brand-form-submit">
            {busy ? "Adding…" : "Add asset"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
